<template>
    <div class="srv-recap">
        <header class="srv-head">
            <button class="srv-icone-btn" aria-label="Retour" @click="$emit('retour')">
                <i class="ri-arrow-left-line"></i>
            </button>
            <div>
                <div class="srv-head-titre">{{ brouillon.table ? brouillon.table.name : 'Commande' }}</div>
                <div class="srv-head-sous srv-num">
                    {{ brouillon.numero }}<span v-if="clientLabel"> · {{ clientLabel }}</span><span v-if="dejaEnregistree"> · {{ libelleStatut }}</span>
                </div>
            </div>
        </header>

        <div class="srv-corps">
            <!-- Renommage du client : plusieurs additions peuvent coexister sur
                 une table, un nom parlant (« Dame en rouge ») aide à les suivre.
                 Absent en mode sans table (pas de client). -->
            <div v-if="brouillon.client" class="srv-client-edit">
                <i class="ri-user-line"></i>
                <input type="text" class="srv-client-inp"
                       :value="brouillon.client.nom"
                       :placeholder="'Client ' + brouillon.client.num"
                       @change="$emit('renommer', $event.target.value)" />
            </div>

            <div v-if="!brouillon.lignes.length" class="srv-vide">
                <i class="ri-shopping-basket-line"></i>
                <p>Commande vide</p>
                <small>Ajoutez au moins un article.</small>
            </div>

            <div v-for="l in brouillon.lignes" :key="l.lineId"
                 class="srv-ligne" :class="{ 'srv-ligne-annulee': l.qte === 0 }">
                <div class="srv-ligne-txt">
                    <div class="srv-ligne-nom">
                        {{ l.libelle }}
                        <!-- Badge d'envoi : le serveur doit voir d'un regard ce
                             qui partira au prochain « Passer la commande » et ce
                             qui est déjà en cuisine. -->
                        <span v-if="badge(l)" class="srv-badge" :class="'srv-badge-' + badge(l).ton">
                            {{ badge(l).txt }}
                        </span>
                    </div>
                    <!-- Portion, accompagnement et note en sous-lignes grises :
                         le serveur doit pouvoir relire la commande d'un coup
                         d'œil avant de l'envoyer. -->
                    <div class="srv-ligne-sous srv-num">{{ l.tarifLibelle }}</div>
                    <div v-if="l.accompagnement" class="srv-ligne-sous">
                        <i class="ri-restaurant-line"></i> {{ l.accompagnement }}
                    </div>
                    <div v-if="texteIngredients(l)" class="srv-ligne-sous srv-ligne-ing">
                        <i class="ri-scissors-cut-line"></i> {{ texteIngredients(l) }}
                    </div>
                    <div v-if="l.note" class="srv-ligne-note">
                        <i class="ri-chat-1-line"></i> {{ l.note }}
                    </div>
                </div>

                <div class="srv-ligne-d">
                    <div class="srv-ligne-mtnt srv-num">{{ fmt(l.pu * l.qte) }}</div>
                    <div class="srv-stepper">
                        <button aria-label="Retirer" @click="$emit('quantite', l, -1)">
                            <i class="ri-subtract-line"></i>
                        </button>
                        <span class="srv-num">{{ l.qte }}</span>
                        <button aria-label="Ajouter" @click="$emit('quantite', l, 1)">
                            <i class="ri-add-line"></i>
                        </button>
                    </div>
                    <button class="srv-suppr" aria-label="Supprimer" @click="$emit('supprimer', l)">
                        <i class="ri-delete-bin-line"></i>
                    </button>
                </div>
            </div>

            <button class="srv-btn srv-btn-sec srv-ajouter" @click="$emit('ajouter')">
                <i class="ri-add-line"></i>
                <span>Ajouter un article</span>
            </button>

            <div class="srv-totaux srv-carte">
                <div class="srv-totaux-l">
                    <span>{{ nbArticles }} article{{ nbArticles > 1 ? 's' : '' }}</span>
                    <span class="srv-num">{{ fmt(total) }}</span>
                </div>
                <div class="srv-totaux-l total">
                    <span>Total</span>
                    <span class="srv-num">{{ fmt(total) }}</span>
                </div>
            </div>
        </div>

        <!-- Trois sorties, et c'est le cœur du parcours :
             « Passer la commande » (principal) envoie en cuisine les seuls
             articles neufs - répétable au fil du repas. « Enregistrer » met de
             côté sans rien envoyer. « Encaisser » clôt et produit le QR de
             paiement (chemin direct conservé). -->
        <div class="srv-barre srv-barre-triple">
            <button class="srv-btn srv-btn-passer" :disabled="!aDelta" @click="$emit('passer-commande')">
                <i class="ri-send-plane-line"></i>
                <span>Passer la commande<template v-if="nbNouveau"> ({{ nbNouveau }})</template></span>
            </button>
            <div class="srv-barre-sec">
                <button class="srv-btn srv-btn-sec" :disabled="!brouillon.lignes.length"
                        @click="$emit('enregistrer')">
                    <i class="ri-save-line"></i>
                    <span>Enregistrer</span>
                </button>
                <button class="srv-btn srv-btn-sec" :disabled="!aRegler" @click="$emit('passer-caisse')">
                    <i class="ri-qr-code-line"></i>
                    <span>Encaisser</span>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
    /* eslint-disable */
    import { fmt } from "../menu/format";
    import { STATUTS, etatLigne, deltaLigne, aDelta, labelClient, texteIngredients } from "./statuts";

    export default {
        name: "srvrecapitulatif",
        props: {
            brouillon: { type: Object, required: true },
            dejaEnregistree: { type: Boolean, default: false },
        },
        emits: ["ajouter", "quantite", "supprimer", "renommer", "enregistrer", "passer-commande", "passer-caisse", "retour"],
        computed: {
            clientLabel() { return labelClient(this.brouillon); },
            nbArticles() { return this.brouillon.lignes.reduce((s, l) => s + l.qte, 0); },
            total() { return this.brouillon.lignes.reduce((s, l) => s + l.pu * l.qte, 0); },
            // Y a-t-il du neuf ou une annulation à envoyer en cuisine ?
            aDelta() { return aDelta(this.brouillon); },
            // Encaisser n'a de sens que s'il reste quelque chose à régler.
            aRegler() { return this.total > 0; },
            // Nombre d'articles neufs (ajouts seuls, pas les annulations) pour
            // le compteur du bouton.
            nbNouveau() {
                return this.brouillon.lignes.reduce((s, l) => s + Math.max(0, deltaLigne(l)), 0);
            },
            libelleStatut() {
                const s = STATUTS[this.brouillon.statut];
                return s ? s.libelle : "Ouverte";
            },
        },
        methods: {
            fmt,
            texteIngredients,
            badge(l) {
                const d = deltaLigne(l);
                switch (etatLigne(l)) {
                    case "nouveau": return { txt: "Nouveau", ton: "neuf" };
                    case "ajout":   return { txt: "+" + d,    ton: "neuf" };
                    case "retrait": return { txt: "−" + (-d), ton: "annul" };
                    case "annule":  return { txt: "Annulé",   ton: "annul" };
                    case "envoye":  return { txt: "Envoyé",   ton: "envoye" };
                    default:        return null;
                }
            },
        },
    };
</script>

<style scoped>
    .srv-recap { display: flex; flex-direction: column; min-height: 100vh; }

    .srv-client-edit {
        display: flex; align-items: center; gap: 10px;
        min-height: var(--srv-tap); padding: 0 14px; margin-bottom: var(--srv-md);
        background: var(--srv-primary-soft);
        border: 1px solid var(--srv-primary); border-radius: var(--srv-r-input);
    }
    .srv-client-edit i { font-size: 18px; color: var(--srv-primary); }
    .srv-client-inp {
        flex: 1; min-width: 0; background: none; border: none; outline: none;
        color: var(--srv-on); font-family: inherit; font-size: 15px; font-weight: 700;
    }
    .srv-client-inp::placeholder { color: var(--srv-primary); font-weight: 700; opacity: .8; }

    .srv-ligne {
        display: flex; gap: var(--srv-md); padding: 14px 0;
        border-bottom: 1px solid var(--srv-outline-variant);
    }
    .srv-ligne-txt { flex: 1; min-width: 0; }
    .srv-ligne-nom {
        font-size: 16px; font-weight: 700;
        display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
    }

    /* Ligne annulée (ramenée à zéro après envoi) : barrée et estompée, mais
       toujours visible tant que l'annulation n'est pas partie en cuisine. */
    .srv-ligne-annulee { opacity: .6; }
    .srv-ligne-annulee .srv-ligne-nom,
    .srv-ligne-annulee .srv-ligne-mtnt { text-decoration: line-through; }
    .srv-ligne-annulee .srv-badge { text-decoration: none; }

    /* Badges d'état d'envoi. Vert = du neuf à passer ; rouge = une annulation
       à transmettre ; gris = déjà en cuisine, rien à faire. */
    .srv-badge {
        display: inline-flex; align-items: center;
        padding: 2px 8px; border-radius: var(--srv-r-chip);
        font-size: 10px; font-weight: 800; text-transform: uppercase; letter-spacing: .04em;
        white-space: nowrap;
    }
    .srv-badge-neuf   { background: var(--srv-success-soft); color: var(--srv-primary); }
    .srv-badge-annul  { background: var(--srv-error-soft);   color: var(--srv-error); }
    .srv-badge-envoye { background: var(--srv-surface-highest); color: var(--srv-on-variant); }
    .srv-ligne-sous {
        display: flex; align-items: center; gap: 5px; margin-top: 3px;
        font-size: 13px; color: var(--srv-on-variant);
    }
    .srv-ligne-sous i { font-size: 14px; }
    .srv-ligne-ing { color: var(--srv-warn-on); font-weight: 600; }
    .srv-ligne-note {
        display: flex; align-items: center; gap: 5px; margin-top: 3px;
        font-size: 12px; font-style: italic; color: var(--srv-outline);
    }

    .srv-ligne-d { display: flex; flex-direction: column; align-items: flex-end; gap: var(--srv-sm); flex-shrink: 0; }
    .srv-ligne-mtnt { font-size: 16px; font-weight: 800; white-space: nowrap; }

    .srv-stepper {
        display: flex; align-items: center;
        background: var(--srv-surface); border: 1px solid var(--srv-outline-variant);
        border-radius: var(--srv-r);
    }
    .srv-stepper button {
        width: 40px; height: 40px; background: none; border: none;
        color: var(--srv-primary); font-size: 18px; cursor: pointer;
    }
    .srv-stepper span { min-width: 24px; text-align: center; font-size: 15px; font-weight: 800; }

    .srv-suppr {
        background: none; border: none; color: var(--srv-outline);
        font-size: 18px; cursor: pointer; padding: 4px;
    }
    .srv-suppr:hover { color: var(--srv-error); }

    .srv-ajouter { margin-top: var(--srv-md); }

    /* « Enregistrer » plus étroit que « Passer à la caisse » : la sortie
       principale doit rester la plus large sous le pouce. */
    .srv-barre-double { display: grid; grid-template-columns: 1fr 1.4fr; gap: var(--srv-sm); }

    /* Barre à trois sorties : « Passer la commande » pleine largeur au-dessus
       (l'action répétée du service), les deux sorties de fin en dessous. */
    .srv-barre-triple { display: flex; flex-direction: column; gap: var(--srv-sm); }
    .srv-barre-sec { display: grid; grid-template-columns: 1fr 1fr; gap: var(--srv-sm); }
    .srv-btn-passer:disabled { background: #6b7280; box-shadow: none; opacity: .55; }

    .srv-totaux { margin-top: var(--srv-md); padding: var(--srv-md); }
    .srv-totaux-l {
        display: flex; align-items: center; justify-content: space-between;
        font-size: 14px; color: var(--srv-on-variant);
    }
    .srv-totaux-l.total {
        margin-top: var(--srv-sm); padding-top: var(--srv-sm);
        border-top: 1px solid var(--srv-outline-variant);
        font-size: 19px; font-weight: 800; color: var(--srv-on);
    }
</style>
